import React, { useState } from 'react';
import {
  CarInput,
  CarResponse,
  Condition,
  Drive,
  formatPrice,
  Fuel,
  getCurrentYear,
  Manufacturer,
  PaintColor,
  Size,
  State,
  TitleStatus,
  Transmission,
  VehicleType,
} from '../types/usedCars';
import { api } from '../services/api';
import { getEnumLabel } from '../utils/formatters';
import './UsedCarsForm.css';

const UsedCarsForm: React.FC = () => {
  const currentYear = getCurrentYear();

  // Initial form state
  const initialFormState: CarInput = {
    manufacturer: Manufacturer.TOYOTA,
    fuel: Fuel.GAS,
    transmission: Transmission.AUTOMATIC,
    drive: Drive.FRONT_WHEEL_DRIVE,
    size: Size.MID_SIZE,
    vehicle_type: VehicleType.SEDAN,
    paint_color: PaintColor.WHITE,
    state: State.CALIFORNIA,
    title_status: TitleStatus.CLEAN,
    condition: Condition.GOOD,
    year: currentYear - 5, // Default to 5 years old
    odometer: 50000, // Default mileage
    cylinders: 4, // Default cylinders
  };

  // Form state
  const [formData, setFormData] = useState<CarInput>(initialFormState);
  const [result, setResult] = useState<CarResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Handle form input changes
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;

    // Handle numeric inputs
    if (name === 'year' || name === 'odometer' || name === 'cylinders') {
      setFormData({
        ...formData,
        [name]: parseInt(value, 10),
      });
    } else {
      setFormData({
        ...formData,
        [name]: value,
      });
    }
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const response = await api.predictCarPrice(formData);

      if (response.status === 'success' && response.data) {
        setResult(response.data);
      } else {
        setError(response.error || 'An error occurred while predicting the car price.');
      }
    } catch (err) {
      setError('An unexpected error occurred. Please try again.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Create dropdown options from enum
  const renderEnumOptions = (enumObj: Record<string, string>) => {
    return Object.entries(enumObj).map(([key, value]) => (
      <option key={value} value={value}>
        {getEnumLabel(key)}
      </option>
    ));
  };

  return (
    <div className="used-cars-form">
      <h2>Used Car Price Predictor</h2>
      <p>
        Fill out the form below to get an estimated price for a used car based on its
        characteristics.
      </p>

      <form onSubmit={handleSubmit}>
        <div className="form-section">
          <h3>Vehicle Information</h3>

          <div className="form-group">
            <label htmlFor="manufacturer">Manufacturer:</label>
            <select
              id="manufacturer"
              name="manufacturer"
              value={formData.manufacturer}
              onChange={handleChange}
              required
            >
              {renderEnumOptions(Manufacturer)}
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="vehicle_type">Vehicle Type:</label>
            <select
              id="vehicle_type"
              name="vehicle_type"
              value={formData.vehicle_type}
              onChange={handleChange}
              required
            >
              {renderEnumOptions(VehicleType)}
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="size">Size:</label>
            <select id="size" name="size" value={formData.size} onChange={handleChange} required>
              {renderEnumOptions(Size)}
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="year">Year:</label>
            <input
              type="number"
              id="year"
              name="year"
              value={formData.year}
              onChange={handleChange}
              min={1994}
              max={currentYear}
              required
            />
          </div>
        </div>

        <div className="form-section">
          <h3>Technical Specifications</h3>

          <div className="form-group">
            <label htmlFor="fuel">Fuel Type:</label>
            <select id="fuel" name="fuel" value={formData.fuel} onChange={handleChange} required>
              {renderEnumOptions(Fuel)}
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="transmission">Transmission:</label>
            <select
              id="transmission"
              name="transmission"
              value={formData.transmission}
              onChange={handleChange}
              required
            >
              {renderEnumOptions(Transmission)}
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="drive">Drive Type:</label>
            <select id="drive" name="drive" value={formData.drive} onChange={handleChange} required>
              {renderEnumOptions(Drive)}
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="cylinders">Cylinders:</label>
            <input
              type="number"
              id="cylinders"
              name="cylinders"
              value={formData.cylinders}
              onChange={handleChange}
              min={3}
              max={12}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="odometer">Odometer (miles):</label>
            <input
              type="number"
              id="odometer"
              name="odometer"
              value={formData.odometer}
              onChange={handleChange}
              min={0}
              required
            />
          </div>
        </div>

        <div className="form-section">
          <h3>Condition & Appearance</h3>

          <div className="form-group">
            <label htmlFor="condition">Condition:</label>
            <select
              id="condition"
              name="condition"
              value={formData.condition}
              onChange={handleChange}
              required
            >
              {renderEnumOptions(Condition)}
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="paint_color">Paint Color:</label>
            <select
              id="paint_color"
              name="paint_color"
              value={formData.paint_color}
              onChange={handleChange}
              required
            >
              {renderEnumOptions(PaintColor)}
            </select>
          </div>
        </div>

        <div className="form-section">
          <h3>Location & Title</h3>

          <div className="form-group">
            <label htmlFor="state">State:</label>
            <select id="state" name="state" value={formData.state} onChange={handleChange} required>
              {renderEnumOptions(State)}
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="title_status">Title Status:</label>
            <select
              id="title_status"
              name="title_status"
              value={formData.title_status}
              onChange={handleChange}
              required
            >
              {renderEnumOptions(TitleStatus)}
            </select>
          </div>
        </div>

        <button type="submit" className="submit-button" disabled={loading}>
          {loading ? 'Predicting...' : 'Predict Price'}
        </button>
      </form>

      {error && (
        <div className="error-message">
          <p>{error}</p>
        </div>
      )}

      {result && (
        <div className="result-container">
          <h3>Prediction Result</h3>
          <p className="prediction">The estimated price is {formatPrice(result.price)}</p>
        </div>
      )}
    </div>
  );
};

export default UsedCarsForm;
